import { API_CONFIG } from '../config/api';

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: any;
  statusCode?: number;
}

// Request configuration
export interface RequestConfig {
  timeout?: number;
  retries?: number;
  requireAuth?: boolean;
  headers?: Record<string, string>;
}

// Token management
class TokenManager {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiry: number | null = null;

  setTokens(accessToken: string, refreshToken: string, expiresIn: number) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.tokenExpiry = Date.now() + (expiresIn * 1000);
    
    // Store in localStorage for persistence
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('token_expiry', this.tokenExpiry.toString());
  }

  getAccessToken(): string | null {
    // Check if token exists and is not expired
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }
    
    // Try to load from localStorage
    const storedToken = localStorage.getItem('access_token');
    const storedExpiry = localStorage.getItem('token_expiry');
    
    if (storedToken && storedExpiry && Date.now() < parseInt(storedExpiry)) {
      this.accessToken = storedToken;
      this.tokenExpiry = parseInt(storedExpiry);
      return storedToken;
    }
    
    return null;
  }

  getRefreshToken(): string | null {
    return this.refreshToken || localStorage.getItem('refresh_token');
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
  }

  isTokenExpired(): boolean {
    return !this.tokenExpiry || Date.now() >= this.tokenExpiry;
  }
}

// Base API service class
export class BaseApiService {
  private tokenManager = new TokenManager();
  private baseURL: string;

  constructor(baseURL: string = API_CONFIG.baseURL) {
    this.baseURL = baseURL;
  }

  // HTTP request method with error handling and retries
  protected async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      timeout = API_CONFIG.security.apiTimeout,
      retries = API_CONFIG.security.maxRetries,
      requireAuth = false,
      headers = {}
    } = config;

    const url = `${this.baseURL}${endpoint}`;
    
    // Prepare headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Client-Version': '1.0.0',
      'X-Request-ID': this.generateRequestId(),
      ...headers
    };

    // Add authentication if required
    if (requireAuth) {
      const token = this.tokenManager.getAccessToken();
      if (!token) {
        return {
          success: false,
          error: 'AUTHENTICATION_REQUIRED',
          message: 'Authentication token required',
          timestamp: new Date().toISOString()
        };
      }
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Setup timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    let lastError: ApiError | null = null;

    // Retry logic
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: requestHeaders,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Handle authentication errors
        if (response.status === 401 && requireAuth) {
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            // Retry with new token
            requestHeaders['Authorization'] = `Bearer ${this.tokenManager.getAccessToken()}`;
            continue;
          } else {
            this.tokenManager.clearTokens();
            return {
              success: false,
              error: 'AUTHENTICATION_FAILED',
              message: 'Authentication failed. Please log in again.',
              timestamp: new Date().toISOString()
            };
          }
        }

        // Parse response
        const responseData = await response.json();

        if (response.ok) {
          return {
            success: true,
            data: responseData,
            timestamp: new Date().toISOString()
          };
        } else {
          lastError = {
            code: responseData.code || 'API_ERROR',
            message: responseData.message || 'An error occurred',
            details: responseData.details,
            statusCode: response.status
          };
        }

      } catch (error: any) {
        clearTimeout(timeoutId);
        
        if (error.name === 'AbortError') {
          lastError = {
            code: 'TIMEOUT',
            message: 'Request timed out',
            details: { timeout, attempt: attempt + 1 }
          };
        } else {
          lastError = {
            code: 'NETWORK_ERROR',
            message: error.message || 'Network error occurred',
            details: { attempt: attempt + 1 }
          };
        }
      }

      // Wait before retry (exponential backoff)
      if (attempt < retries) {
        await this.delay(Math.pow(2, attempt) * 1000);
      }
    }

    return {
      success: false,
      error: lastError?.code || 'UNKNOWN_ERROR',
      message: lastError?.message || 'An unknown error occurred',
      timestamp: new Date().toISOString()
    };
  }

  // Refresh access token
  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.tokenManager.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseURL}${API_CONFIG.auth.endpoints.refreshToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        this.tokenManager.setTokens(data.accessToken, data.refreshToken, data.expiresIn);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }

    return false;
  }

  // Utility methods
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  protected delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Public methods for token management
  public setAuthTokens(accessToken: string, refreshToken: string, expiresIn: number) {
    this.tokenManager.setTokens(accessToken, refreshToken, expiresIn);
  }

  public clearAuthTokens() {
    this.tokenManager.clearTokens();
  }

  public isAuthenticated(): boolean {
    return !this.tokenManager.isTokenExpired() && this.tokenManager.getAccessToken() !== null;
  }

  // HTTP method helpers
  protected async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'GET' }, config);
  }

  protected async post<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(
      endpoint, 
      { 
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined
      }, 
      config
    );
  }

  protected async put<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined
      },
      config
    );
  }

  protected async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'DELETE' }, config);
  }
}